import { useEffect, useState } from 'react'
import { useTheme } from 'styled-components'

import { VehicleDetailsTooltip } from '@/feature/observatory'
import { FormattedMessage } from 'react-intl'
import { LuArrowBigDown, LuArrowBigUp, LuInfo } from 'react-icons/lu'

import { IconButton } from '@/lib/core/components/Button'
import { CircleAvatar } from '@/lib/core/components/CircleAvatar'
import { Pagination, PaginationPayload } from '@/lib/core/components/Pagination'
import { RadioButtons } from '@/lib/core/components/RadioButtons'
import { Table, TableColumns, Td, Tr } from '@/lib/core/components/Table'
import { Owner, Row } from '@/lib/core/components/Table/Table.styles'
import { Tooltip } from '@/lib/core/components/Tooltip'
import { Truncate } from '@/lib/core/components/Truncate'
import { TypeBadge } from '@/lib/core/components/TypeBadge'
import { SmartShareListFilters, SortDirection, SortInfo } from '@/lib/core/interfaces/common.types'

import { BridgeStatus } from '@/lib/features/Assets/interfaces'
import { useHistorySmartShares } from '@/lib/features/SmartShares/api'
import messages from '@/lib/features/SmartShares/components/HistoryTable/HistoryTable.messages'

import { checkIsDateAndConvertToBackendStandard, formatDate } from '@/lib/utils/date-time'

import { HistorySharesResponse } from '../../interfaces'

export type HistoryTableProps = {
  defaultFilters: SmartShareListFilters
  onFiltersChange?: (filters: SmartShareListFilters) => void
}

const TABLE_COLUMNS: TableColumns = [
  {
    accessor: 'bridge_id',
    label: messages.assetId.defaultMessage,
    sortable: true,
  },
  {
    accessor: 'description',
    sortable: true,
    label: messages.description.defaultMessage,
    sortKey: 'asset__description',
  },
  {
    accessor: 'plateNumber',
    sortable: true,
    label: messages.plateNumber.defaultMessage,
    sortKey: 'plate_number',
  },
  {
    accessor: 'reference',
    sortable: true,
    label: messages.reference.defaultMessage,
  },
  {
    accessor: 'owner',
    sortable: true,
    label: messages.owner.defaultMessage,
    sortKey: 'organization_name',
  },
  {
    accessor: 'type',
    sortable: true,
    label: messages.type.defaultMessage,
    sortKey: 'bridge_status',
  },
  {
    accessor: 'bridge_date_from',
    sortable: true,
    label: messages.bridgeDateFrom.defaultMessage,
  },
  {
    accessor: 'bridge_date_to',
    sortable: true,
    label: messages.bridgeDateTo.defaultMessage,
  },
  {
    accessor: 'actions',
    label: '',
  },
] as const

export const HistoryTable = ({
  defaultFilters = {},
  onFiltersChange = () => null,
}: HistoryTableProps) => {
  const { colors } = useTheme()
  const [filters, setFilters] = useState<SmartShareListFilters>(defaultFilters)
  const list = useHistorySmartShares(filters)
  const isLoading = list.isLoading

  const data =
    (list.data as HistorySharesResponse) ??
    ({
      count: 0,
      countAll: 0,
      countUnseen: 0,
      countLent: 0,
      countBorrowed: 0,
      countOnward: 0,
      next: null,
      previous: null,
      results: [],
    } as HistorySharesResponse)

  const onPaginationChange = (paginationFilters: PaginationPayload) => {
    setFilters({
      ...filters,
      ...paginationFilters,
    })
  }

  const onSearch = (search?: string) => {
    if (search) {
      const convertedDate = checkIsDateAndConvertToBackendStandard(search)
      if (convertedDate) {
        setFilters({
          ...filters,
          page: 1,
          search: convertedDate,
        })
        return
      }
    }

    setFilters({
      ...filters,
      page: 1,
      search,
    })
  }

  const clearSearch = () => {
    setFilters({
      ...filters,
      page: 1,
      search: undefined,
    })
  }

  const onSort = (key: string, direction: SortDirection | undefined) => {
    const newSortInfo: SortInfo = direction ? { [key]: direction } : {}
    setFilters({
      ...filters,
      ordering: newSortInfo,
    })
  }

  // Braces matter: a concise arrow body would return onFiltersChange's result, which React
  // then treats as the effect's cleanup. The default handler returns null, so unmounting a
  // HistoryTable rendered without an onFiltersChange prop threw "destroy is not a function".
  useEffect(() => {
    onFiltersChange(filters)
  }, [filters])

  const onFilterType = (shareType: string) => {
    setFilters({
      ...filters,
      page: 1,
      shareType: shareType,
    })
  }

  return (
    <>
      <Table
        columns={TABLE_COLUMNS}
        isLoading={isLoading}
        isSelectable={false}
        elementsCounter={data.count}
        onSearch={onSearch}
        clearSearch={clearSearch}
        onSort={onSort}
        sortInfo={filters.ordering}
        renderCustomFilter={
          <RadioButtons
            additionalStyles={{
              minHeight: 40,
            }}
            currentValue={defaultFilters.shareType ?? 'all'}
            name={`smart-share-type-filter`}
            onChange={(value) => {
              if (value === 'all') {
                clearSearch()
              } else {
                onFilterType(value)
              }
            }}
            options={[
              {
                label: `All (${data.countAll ?? 0})`,
                value: 'all',
              },
              {
                label: `Inbound (${data.countBorrowed ?? 0})`,
                value: 'borrowed',
              },
              {
                label: `Outbound (${data.countLent ?? 0})`,
                value: 'lent',
              },
              {
                label: `Onward (${data.countOnward ?? 0})`,
                value: 'onward',
              },
            ]}
          />
        }
      >
        {data.results.map(
          ({
            assetId,
            bridgeId,
            bridgeStatus,
            dateFrom,
            dateTo,
            description,
            contactTo,
            contactFrom,
            id,
            make,
            model,
            organization,
            plateNumber,
            reference,
          }) => (
            <Tr key={id}>
              <Td>
                <Row>
                  {bridgeId}

                  <Tooltip
                    interactive
                    trigger="click"
                    arrowOffset={50}
                    contentPadding={0}
                    placement="left-end"
                    tooltipContent={
                      <VehicleDetailsTooltip
                        make={make}
                        model={model}
                        plateNumber={plateNumber}
                        id={assetId}
                        organization={organization}
                        contactFrom={contactFrom}
                        contactTo={contactTo}
                        bridgeStatus={bridgeStatus}
                      />
                    }
                  >
                    <IconButton type="button" $variant="primary">
                      <LuInfo size="16" color={colors.active} strokeWidth={1.6} />
                    </IconButton>
                  </Tooltip>
                </Row>
              </Td>
              <Td>
                <Truncate content={description} />
              </Td>
              <Td>
                <Truncate content={plateNumber} />
              </Td>
              <Td>
                <Truncate content={reference ?? '-'} />
              </Td>
              <Td>
                <Owner>
                  <CircleAvatar src={organization.avatar} placeholder="empty" size={27.5} />
                  {organization.name}
                </Owner>
              </Td>
              <Td>
                {bridgeStatus === BridgeStatus.BORROWED && (
                  <TypeBadge $variant="success">
                    <LuArrowBigDown size="16" />
                    <FormattedMessage {...messages.in} />
                  </TypeBadge>
                )}
                {bridgeStatus === BridgeStatus.LENT && (
                  <TypeBadge $variant="warning">
                    <LuArrowBigUp size="16" />
                    <FormattedMessage {...messages.out} />
                  </TypeBadge>
                )}
              </Td>
              <Td>{formatDate(dateFrom)}</Td>
              <Td>{formatDate(dateTo)}</Td>
            </Tr>
          ),
        )}
      </Table>
      <Pagination
        initialPage={filters.page}
        initialPageSize={filters.pageSize}
        isLoading={isLoading}
        onChange={onPaginationChange}
        recordsCount={data.count}
      />
    </>
  )
}
